
import { Card, DatePicker, Empty, Form, Table } from 'antd';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import ModelBreadcrumbs from '../../../components/ModelBreadcrumbs';
import { RefreshButton } from '../../../components/RefreshButton';
import { ITab, TabsManager } from '../../../components/TabsManager';
import { apiClient } from '../../../graphql/api/apiClient';
import { usefunnelAppointmentsAuditQuery } from '../../../graphql/api/generated';
import { useTabSelectorWithState } from '../../../hooks/useTabSelector';
import BasicLayout from '../../../layouts/BasicLayout';
import ErrorLayout from '../../../layouts/ErrorLayout';
import { alphanumericSorter, formatPhoneNumber, formatTimestamp, formatUserName, TIME_FORMAT } from '../../../utils';
import { FunnelAppointmentStatus } from '../components/FunnelAppointmentStatus';
import { PropertyModel } from '../model';

export const PropertyFunnelAppointmentsPage: React.FC<RouteComponentProps<{ propertyId: string }>> = (props) => {
  const { propertyId } = props.match.params;
  const [date, setDate] = useState<Moment>(moment(new Date()));

  const tabSelector = useTabSelectorWithState({
    defaultActiveKey: 'BOOKED_APPOINTMENTS',
    allowedKeys: ['AVAILABLE_APPOINTMENT_TIMES', 'BOOKED_APPOINTMENTS'],
  });

  const { previousData, data, error, loading, refetch } = usefunnelAppointmentsAuditQuery({
    client: apiClient,
    variables: {
      input: { propertyId, date: date.format('YYYY-MM-DD') },
    },
  });

  const funnelAudit = data?.funnelAppointmentsAudit || previousData?.funnelAppointmentsAudit;
  const property = funnelAudit?.property;
  const bookedAppointments = funnelAudit?.bookedAppointments || [];
  const availableAppointmentTimes = funnelAudit?.availableAppointmentTimes || [];

  const pageTitle = 'Funnel Appointments';

  if (error) {
    return <ErrorLayout pageTitle={pageTitle} error={error} />;
  }

  const tabs: ITab[] = [
    {
      loading,
      key: 'BOOKED_APPOINTMENTS',
      title: `Booked Appointments (${bookedAppointments.length})`,
      empty: false,
      content: () => (
        <Table
          loading={{ size: 'large', spinning: loading }}
          rowKey={a => a.funnelAppointmentId}
          dataSource={bookedAppointments}
          scroll={{ x: true }}
          columns={[
            {
              title: 'Appointment ID',
              render: (_, { funnelAppointmentId }) => funnelAppointmentId,
            },
            {
              title: 'Appointment Status',
              render: (_, { status }) => <FunnelAppointmentStatus status={status} />,
            },
            {
              title: 'Start Time',
              render: (_, { startTime }) => formatTimestamp(startTime),
              defaultSortOrder: 'descend',
              sortDirections: ['ascend', 'descend', 'ascend'],
              sorter: (appointmentA, appointmentB) => {
                return alphanumericSorter(
                  appointmentA.startTime,
                  appointmentB.startTime,
                );
              },
            },
            {
              title: 'Prospect Name',
              render: (_, { client }) => formatUserName(client),
            },
            {
              title: 'Phone Number',
              render: (_, { client }) => formatPhoneNumber(client?.phoneNumber),
            },
            {
              title: 'Email',
              render: (_, { client }) => client?.email,
            },
          ]}
          showSorterTooltip={false}
          pagination={false}
        />
      ),
    },
    {
      loading,
      key: 'AVAILABLE_APPOINTMENT_TIMES',
      title: `Available Appointment Times (${availableAppointmentTimes.length})`,
      empty: false,
      content: () => {
        if (!availableAppointmentTimes.length) {
          return <Empty description='No available appointment times' />;
        }

        return (
          <ul>
            {availableAppointmentTimes.map(time => (
              <li key={time}>{moment(time).format(TIME_FORMAT)}</li>
            ))}
          </ul>
        );
      },
    },
  ];

  return (
    <BasicLayout pageTitle={pageTitle}>
      <Card
        title={(
          <ModelBreadcrumbs
            model={PropertyModel}
            rowBreadCrumb={{ row: property }}
            customBreadCrumbs={[pageTitle]}
          />
        )}
        bordered={false}
        style={{ marginBottom: 20 }}
        extra={(
          <RefreshButton
            disabled={loading}
            refresh={refetch}
            useSubscription={false}
          />
        )}
      >
        <Form.Item
          label='Date'
          style={{ marginBottom: 25 }}
          className='label-align-top'
          required
        >
          <DatePicker
            format='MMM D, YYYY'
            showTime={false}
            allowClear={false}
            placeholder='Date'
            value={date}
            onChange={(nextDate) => {
              setDate(nextDate || moment(new Date()));
            }}
          />
        </Form.Item>
        <TabsManager
          tabs={tabs}
          tabSelector={tabSelector}
          cardBodyStyle={{ padding: 0 }}
        />
      </Card>
    </BasicLayout>
  );
}
